import { db, doc, getDoc, setDoc, updateDoc, collection, query, where, getDocs, onSnapshot, serverTimestamp } from '../firebase';
import { getSafeSolicitationDocId, ensureFirestoreSafeSize } from '../utils/firestoreSanitizer';

export interface CentralRfxItem {
  solicitationId: string;
  title: string;
  state: string;
  stateCode: string;
  agency: string;
  description: string;
  dueDate: string;
  status: 'open' | 'closed' | 'awarded';
  sourceUrl: string;
  category: string;
  pricing?: number;
  importSource?: string;
  verifiedLive?: boolean;
  verificationConfidence?: string;
  groundedSourceUrl?: string;
  crawledAt?: string;
  lastScannedAt?: string;
  antiHallucinationVerified?: boolean;
  portalHost?: string;
  details?: {
    importSource?: string;
    verifiedLive?: boolean;
    generalinformation?: {
      "Solicitation Name"?: string;
      "Code"?: string;
      "Round "?: string;
      "Begin"?: string;
      estimatedBudget?: string;
      [key: string]: any;
    };
    response?: {
      rows?: string[][] | Array<Record<string, string>>;
      [key: string]: any;
    };
    solicitationdocuments?: Array<{
      name: string;
      url: string;
      version: number;
    }>;
  };
}

export interface UserRfxItem extends CentralRfxItem {
  id: string; // {userId}_{solicitationId}
  userId: string;
  participationVerified: boolean;
  stateVendorId?: string;
  copiedFromCentralAt: string;
  userProposalStatus?: 'not_started' | 'drafting' | 'ready' | 'submitted' | 'won' | 'archived';
  userNotes?: string;
  customPricing?: number;
}

export interface StateTableMeta {
  stateCode: string;
  stateName: string;
  tableName: string; // e.g. central_rfx_tx
  portalUrl: string;
  totalCount: number;
  openCount: number;
  lastScannedAt: string;
  lastDeltaUpdateAt?: string;
}

export const centralRfxService = {
  /**
   * Helper to format the collection name for a given state code
   */
  getStateCollectionName(stateCode: string): string {
    return `central_rfx_${stateCode.trim().toLowerCase()}`;
  },

  /**
   * Fetch all Central RFX items from the server API
   */
  async fetchCentralRfxItems(filters: {
    stateCode?: string;
    status?: string;
    category?: string;
    search?: string;
    limit?: number;
  } = {}): Promise<CentralRfxItem[]> {
    const params = new URLSearchParams();
    if (filters.stateCode && filters.stateCode !== 'ALL') params.append('stateCode', filters.stateCode);
    if (filters.status && filters.status !== 'ALL') params.append('status', filters.status);
    if (filters.category && filters.category !== 'All') params.append('category', filters.category);
    if (filters.search) params.append('search', filters.search);
    if (filters.limit) params.append('limit', String(filters.limit));

    try {
      const res = await fetch(`/api/central-rfx?${params.toString()}`);
      if (!res.ok) {
        throw new Error(`Failed to fetch central RFX items: ${res.statusText}`);
      }
      const data = await res.json();
      return Array.isArray(data) ? data : (data.items || []);
    } catch (err) {
      console.warn('[Central RFX] API fetch failed, falling back to direct Firestore read:', err);
      // Fallback: If stateCode specified, query Firestore directly
      if (filters.stateCode && filters.stateCode !== 'ALL') {
        const collName = this.getStateCollectionName(filters.stateCode);
        const snapshot = await getDocs(collection(db, collName));
        return snapshot.docs.map(d => ({ solicitationId: d.id, ...d.data() } as CentralRfxItem));
      }
      return [];
    }
  },

  /**
   * Fetch list of state tables and metadata
   */
  async fetchStateTablesMeta(): Promise<StateTableMeta[]> {
    try {
      const res = await fetch('/api/central-rfx/states');
      if (res.ok) {
        return await res.json();
      }
    } catch (err) {
      console.warn('[Central RFX] States meta fetch error:', err);
    }
    return [];
  },

  /**
   * Check if an end user already copied/participated in this solicitation
   */
  async isUserParticipating(userId: string, solicitationId: string): Promise<boolean> {
    if (!userId || !solicitationId) return false;
    try {
      const safeId = `${userId}_${getSafeSolicitationDocId(solicitationId)}`;
      const docSnap = await getDoc(doc(db, 'user_rfx_items', safeId));
      return docSnap.exists();
    } catch (err) {
      return false;
    }
  },

  /**
   * Copy a Central RFX item into the End User's Own Database
   * after intent confirmation and state vendor verification
   */
  async copyToUserDatabase(params: {
    userId: string;
    solicitation: CentralRfxItem;
    stateVendorId?: string;
  }): Promise<UserRfxItem> {
    const { userId, solicitation, stateVendorId } = params;
    if (!userId) throw new Error('User authentication required to participate.');
    if (!solicitation || !solicitation.solicitationId) throw new Error('Invalid solicitation parameter.');

    const safeDocId = `${userId}_${getSafeSolicitationDocId(solicitation.solicitationId)}`;
    const nowIso = new Date().toISOString();

    const userRfxItem: UserRfxItem = {
      ...solicitation,
      id: safeDocId,
      userId,
      participationVerified: true,
      stateVendorId: stateVendorId || '',
      copiedFromCentralAt: nowIso,
      userProposalStatus: 'drafting',
      userNotes: '',
      customPricing: solicitation.pricing || 75000
    };

    const firestoreSafe = ensureFirestoreSafeSize(userRfxItem);

    await setDoc(doc(db, 'user_rfx_items', safeDocId), {
      ...firestoreSafe,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });

    return userRfxItem;
  },

  /**
   * Update a user's personal RFX item
   */
  async updateUserRfxItem(
    userId: string,
    solicitationId: string,
    updates: Partial<UserRfxItem>
  ): Promise<void> {
    const safeDocId = `${userId}_${getSafeSolicitationDocId(solicitationId)}`;
    await updateDoc(doc(db, 'user_rfx_items', safeDocId), {
      ...updates,
      updatedAt: serverTimestamp()
    });
  }
};
